/**
 * GoogleMapTracker
 * ─────────────────────────────────────────────
 * • Shows driver location + destination on Google Map
 * • Draws Directions route between them
 * • Auto-fits bounds to both markers
 */
import React, { useEffect, useRef } from 'react';
import useGoogleMaps from '../hooks/useGoogleMaps';

export default function GoogleMapTracker({ driverPos, destPos, height = 240 }) {
  const { loaded, error } = useGoogleMaps();
  const mapRef       = useRef(null);
  const mapObj       = useRef(null);
  const driverMarker = useRef(null);
  const destMarker   = useRef(null);
  const renderer     = useRef(null);

  /* ── build driver SVG marker (water_care icon) ── */
  const driverIcon = () => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="52" height="52" viewBox="0 0 52 52">
        <circle cx="26" cy="26" r="24" fill="#0ABAB5" stroke="white" stroke-width="2.5" opacity="0.95"/>
        <circle cx="26" cy="26" r="20" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="1"/>
        <g transform="translate(10,13)" stroke="white" stroke-width="1.8" fill="none" stroke-linecap="round" stroke-linejoin="round">
          <rect x="0" y="4" width="20" height="13" rx="1.5"/>
          <path d="M20 7h6l3 6v5h-9V7z"/>
          <circle cx="5" cy="19" r="2" fill="white" stroke="none"/>
          <circle cx="24" cy="19" r="2" fill="white" stroke="none"/>
          <path d="M3 10h14" stroke-opacity="0.5"/>
        </g>
      </svg>`)}`,
    scaledSize: new window.google.maps.Size(52, 52),
    anchor: new window.google.maps.Point(26, 26),
  });

  const destIcon = () => ({
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r="20" fill="#10b981" stroke="white" stroke-width="2.5"/>
        <circle cx="22" cy="22" r="16" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="1"/>
        <path fill="white" d="M22 10 C17 14.5 13 18.5 13 22.5 a9 9 0 0 0 18 0 C31 18.5 27 14.5 22 10z"/>
        <circle cx="22" cy="22" r="3.5" fill="#10b981"/>
        <line x1="22" y1="31" x2="22" y2="35" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>`)}`,
    scaledSize: new window.google.maps.Size(44, 44),
    anchor: new window.google.maps.Point(22, 44),
  });

  /* ── draw directions ── */
  const drawDirections = (map, origin, destination) => {
    if (!renderer.current) {
      renderer.current = new window.google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#0ABAB5',
          strokeWeight: 3,
          strokeOpacity: 0,
          icons: [{
            icon: {
              path: 'M 0,-1 0,1',
              strokeOpacity: 1,
              strokeWeight: 3,
              strokeColor: '#0ABAB5',
              scale: 3,
            },
            offset: '0',
            repeat: '16px',
          }],
        },
      });
      renderer.current.setMap(map);
    }
    const svc = new window.google.maps.DirectionsService();
    svc.route({
      origin,
      destination,
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (status === 'OK') renderer.current.setDirections(result);
    });
  };

  /* ── fit bounds ── */
  const fitBounds = (map, positions) => {
    const bounds = new window.google.maps.LatLngBounds();
    positions.forEach(p => bounds.extend(p));
    map.fitBounds(bounds, { top: 50, right: 30, bottom: 30, left: 30 });
  };

  /* ── init map ── */
  useEffect(() => {
    if (!loaded || !mapRef.current) return;

    const center = driverPos
      ? { lat: driverPos[0], lng: driverPos[1] }
      : destPos
        ? { lat: destPos[0], lng: destPos[1] }
        : { lat: 24.7136, lng: 46.6753 };

    if (!mapObj.current) {
      mapObj.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 13,
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: window.google.maps.ControlPosition.LEFT_BOTTOM,
        },
        clickableIcons: false,
      });
    }

    const map = mapObj.current;
    const positions = [];

    if (driverPos) {
      const pos = { lat: driverPos[0], lng: driverPos[1] };
      positions.push(pos);
      if (!driverMarker.current) {
        driverMarker.current = new window.google.maps.Marker({
          position: pos, map, icon: driverIcon(), zIndex: 2,
        });
      } else {
        driverMarker.current.setPosition(pos);
      }
    }

    if (destPos) {
      const pos = { lat: destPos[0], lng: destPos[1] };
      positions.push(pos);
      if (!destMarker.current) {
        destMarker.current = new window.google.maps.Marker({
          position: pos, map, icon: destIcon(), zIndex: 1,
        });
      } else {
        destMarker.current.setPosition(pos);
      }
    }

    if (driverPos && destPos) {
      drawDirections(
        map,
        { lat: driverPos[0], lng: driverPos[1] },
        { lat: destPos[0],  lng: destPos[1] },
      );
    }

    if (positions.length > 1) fitBounds(map, positions);
    else if (positions.length === 1) map.panTo(positions[0]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, driverPos, destPos]);

  if (error) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--wt-surface)' }}>
      <p style={{ color: 'var(--wt-text-muted)', fontSize: '0.8rem' }}>تعذّر تحميل الخريطة</p>
    </div>
  );

  if (!loaded) return (
    <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--wt-surface)' }}>
      <div style={{ width: 28, height: 28, borderRadius: '50%',
        border: '3px solid rgba(10,186,181,0.2)', borderTopColor: '#0ABAB5',
        animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return <div ref={mapRef} style={{ width: '100%', height }} />;
}
